import { toast } from "react-toastify";

import { CrmPetModel } from "../../models/crmPet";
import { CRM_PET_RULES } from "../../formatResponses/crmPet";
import { CRM_PET_DEFAULT_FILTER, useCrmPet } from "../../../store/crmPet/useCrmPet";

const VALID_RULES = new Set(["ALL", ...Object.values(CRM_PET_RULES)]);

export default class CrmPetControllerClass {
  constructor() {
    this.name = "crm-pet";
    this.tableName = `${this.name}-table`;
    this.paginationName = `${this.name}-pagination`;
    this.opportunitiesTableName = `${this.name}-opportunities-table`;
    this.opportunitiesPaginationName = `${this.name}-opportunities-pagination`;
    this.pendingTableName = `${this.name}-pending-table`;
    this.pendingPaginationName = `${this.name}-pending-pagination`;
    this.contactedTableName = `${this.name}-contacted-table`;
    this.contactedPaginationName = `${this.name}-contacted-pagination`;
    this.MCrmPet = CrmPetModel();
    this.init();
  }

  init = () => {
    if (typeof window !== "undefined") {
      useCrmPet.getState().actions.setFilter({
        ...(this.isDiagnosisEntry() ? CRM_PET_DEFAULT_FILTER : {}),
        rule: this.getRuleFromUrl(),
      });
      this.syncFilterUrl();
    }
    this.findPets();
    this.findConfig();
  };

  getRuleFromUrl = () => {
    if (typeof window === "undefined") return CRM_PET_DEFAULT_FILTER.rule;
    const params = new URLSearchParams(window.location.search);
    const rule = params.get("rule") || CRM_PET_DEFAULT_FILTER.rule;
    return VALID_RULES.has(rule) ? rule : CRM_PET_DEFAULT_FILTER.rule;
  };

  syncRuleFromUrl = async () => {
    const rule = this.getRuleFromUrl();
    const currentRule = useCrmPet.getState().data.filter.rule;

    if (this.isDiagnosisEntry()) {
      useCrmPet.getState().actions.setFilter({ ...CRM_PET_DEFAULT_FILTER, rule });
      return this.selectRule(rule);
    }
    if (rule === currentRule) return null;
    return this.selectRule(rule);
  };

  isDiagnosisEntry = () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("source") === "diagnosis";

  refreshPets = async () => {
    useCrmPet.getState().actions.clearCustomers({ preserveWhatsApp: true });
    return this.findPets({ force: true });
  };

  setRowContactStatus = async (row, contactDone) => {
    const actions = useCrmPet.getState().actions;
    if (useCrmPet.getState().data.contactMarking[row.pet.petId]) return null;
    actions.setContactMarking(row.pet.petId, true);
    try {
      const result = await this.MCrmPet.setContactStatus({
        customerId: row.customerId,
        petIds: [row.pet.petId],
        membershipIds: row.memberships.map((membership) => membership.membershipId),
        contactDone,
      });
      if (result) {
        actions.setPaginationPage(0);
        await this.refreshPets();
      }
      return result;
    } finally {
      actions.setContactMarking(row.pet.petId, false);
    }
  };

  findPets = async (options) => {
    return this.MCrmPet.findPets(options);
  };

  findEligibleOpportunities = async () => {
    return this.MCrmPet.findEligibleOpportunities();
  };

  loadEligibleOpportunities = async () => {
    const { pets, pagination, isWaiting } =
      useCrmPet.getState().data.opportunities;

    if (!isWaiting && !pets[pagination.page]) {
      await this.findEligibleOpportunities();
    }
  };

  findPendingContacts = async () => {
    return this.MCrmPet.findPendingContacts();
  };

  loadPendingContacts = async () => {
    const { pets, pagination, isWaiting } =
      useCrmPet.getState().data.pending;

    if (!isWaiting && !pets[pagination.page]) {
      await this.findPendingContacts();
    }
  };

  findContactedCustomers = async () => {
    return this.MCrmPet.findContactedCustomers();
  };

  loadContactedCustomers = async () => {
    const { pets, pagination, isWaiting } =
      useCrmPet.getState().data.contacted;

    if (!isWaiting && !pets[pagination.page]) {
      await this.findContactedCustomers();
    }
  };

  findConfig = async () => {
    return this.MCrmPet.findConfig();
  };

  saveConfig = async (config) => {
    const result = await this.MCrmPet.saveConfig(config);
    if (result) {
      const actions = useCrmPet.getState().actions;
      actions.setPaginationPage(0);
      actions.clearCustomers();
      await this.findPets({ force: true });
    }
    return result;
  };

  findCustomerActivePets = async (customerId) => {
    return this.MCrmPet.findCustomerActivePets(customerId);
  };

  registerContact = async (payload) => {
    return this.MCrmPet.registerContact(payload);
  };

  registerUnifiedContact = async (customer, petIds, membershipIds, message) => {
    return this.registerContactAndUpdate({
      customerId: customer.customerId,
      petIds,
      membershipIds,
      message,
    });
  };

  registerRuleContact = async (customer, petIds, ruleCode, message) => {
    return this.registerContactAndUpdate({
      customerId: customer.customerId,
      petIds,
      ruleCode,
      message,
    });
  };

  registerContactAndUpdate = async (payload) => {
    const actions = useCrmPet.getState().actions;
    actions.setContactSubmitting(true);
    try {
      const result = await this.registerContact(payload);
      if (result) {
        actions.applyContactEvent(result);
      }
      return result;
    } finally {
      actions.setContactSubmitting(false);
    }
  };

  registerPetContact = async (pet, contactDone = null) => {
    if (!pet?.petId) return null;

    const actions = useCrmPet.getState().actions;
    const currentDone = useCrmPet.getState().data.contactDone[pet.petId] ?? Boolean(pet.contatoRealizado);
    const done = contactDone ?? !currentDone;
    actions.setContactMarking(pet.petId, true);

    try {
      if (!done) {
        actions.setPetContactDone(pet.petId, false, null);
        return { success: true };
      }

      const result = await this.registerContact({
        petId: pet.petId,
        petName: pet.petName,
        customerId: pet.customerId,
        customerCpf: pet.customerCpf,
        customerName: pet.customerName,
        customerPhone: pet.customerPhone,
        daysSinceLastAppointment: pet.daysSinceLastAppointment,
        lastAppointmentDate: pet.lastAppointmentDate,
        responsavelId: null,
        responsavelNome: null,
      });

      if (!result) return null;

      actions.setPetContactDone(pet.petId, true, result.dataContato || new Date());
      return result;
    } finally {
      actions.setContactMarking(pet.petId, false);
    }
  };

  registerSelectedPetsContact = async (pet, selectedPets = []) => {
    return Promise.all(selectedPets.map((selectedPet) => (
      this.registerPetContact({ ...pet, ...selectedPet }, true)
    )));
  };

  openContactHistory = async (customer) => {
    useCrmPet.getState().actions.openContactHistory(customer);
    return this.MCrmPet.findContactHistory(customer);
  };

  closeContactHistory = () => {
    useCrmPet.getState().actions.closeContactHistory();
  };

  openWhatsApp = async (customerKey) => {
    const state = useCrmPet.getState().data;
    const row = Object.values(state.customers).flat().find((item) => item.customerKey === customerKey);
    if (!row?.customerId) return;
    const requestId = this.whatsappRequest = (this.whatsappRequest || 0) + 1;
    const actions = useCrmPet.getState().actions;
    actions.setSelectedCustomerKey(customerKey);
    actions.setWhatsApp({ loading: true, error: null, customer: null });
    try {
      const customer = await this.MCrmPet.findCustomerOpportunities(row.customerId);
      if (requestId !== this.whatsappRequest) return;
      actions.setWhatsApp({ loading: false, error: null, customer });
    } catch (error) {
      if (requestId !== this.whatsappRequest) return;
      actions.setWhatsApp({ loading: false, error: this.MCrmPet.errorMessage(error, "Não foi possível carregar os pets do tutor."), customer: null });
    }
  };

  closeWhatsApp = () => {
    this.whatsappRequest = (this.whatsappRequest || 0) + 1;
    const actions = useCrmPet.getState().actions;
    actions.setSelectedCustomerKey(null);
    actions.setWhatsApp({ loading: false, error: null, customer: null });
  };

  setBlocked = async (petId, bloqueado = true) => {
    return this.MCrmPet.setBlocked(petId, bloqueado);
  };

  toggleBlocked = async (pet) => {
    if (!pet?.petId) return null;

    const actions = useCrmPet.getState().actions;
    const bloqueado = !(pet.blocked ?? pet.bloqueado);
    actions.setPetBlocking(pet.petId, true);

    try {
      const result = await this.setBlocked(pet.petId, bloqueado);
      if (!result) return null;

      actions.setPetBlocked(
        pet.petId,
        bloqueado,
        bloqueado ? result.dataBloqueio || new Date() : null,
      );
      actions.clearCustomers();
      await this.findPets({ force: true });
      return result;
    } finally {
      actions.setPetBlocking(pet.petId, false);
    }
  };

  setFilter = (filterData) => {
    useCrmPet.getState().actions.setFilter(filterData);
  };

  selectRule = async (rule) => {
    if (!VALID_RULES.has(rule)) return null;

    const actions = useCrmPet.getState().actions;
    actions.setFilter({ rule });
    actions.setSort("customerName", "asc");
    actions.setPaginationPage(0);
    actions.clearCustomers();

    this.syncFilterUrl();
    return this.findPets();
  };

  setOpportunitiesFilter = (filterData) => {
    useCrmPet.getState().actions.setOpportunitiesFilter(filterData);
  };

  setPendingFilter = (filterData) => {
    useCrmPet.getState().actions.setPendingFilter(filterData);
  };

  setContactedFilter = (filterData) => {
    const nextFilter = { ...filterData };

    if (
      nextFilter.diasFiltro &&
      nextFilter.diasFiltro !== "custom"
    ) {
      nextFilter.customDays = nextFilter.diasFiltro;
    }

    useCrmPet.getState().actions.setContactedFilter(nextFilter);
  };

  filterPets = async (event) => {
    if (event) event.preventDefault();

    const actions = useCrmPet.getState().actions;
    actions.setLoading(true);
    actions.setPaginationPage(0);
    actions.clearCustomers();

    this.syncFilterUrl();

    await this.findPets();
  };

  clearFilter = async () => {
    const actions = useCrmPet.getState().actions;
    const { rule } = useCrmPet.getState().data.filter;
    actions.clearFilter();
    actions.setFilter({ rule });
    actions.setPaginationPage(0);
    actions.clearCustomers();

    this.syncFilterUrl();

    await this.findPets();
  };

  filterEligibleOpportunities = async (event) => {
    if (event) event.preventDefault();

    const { from, to } =
      useCrmPet.getState().data.opportunities.filter;

    if (from && to && from > to) {
      toast.error("A data de início não pode ser posterior à data de fim.", {
        theme: "colored",
      });
      return null;
    }

    const actions = useCrmPet.getState().actions;
    actions.setOpportunitiesLoading(true);
    actions.setOpportunitiesPaginationPage(0);
    actions.clearOpportunitiesPets();

    return this.findEligibleOpportunities();
  };

  clearOpportunitiesFilter = async () => {
    const actions = useCrmPet.getState().actions;
    actions.clearOpportunitiesFilter();
    actions.setOpportunitiesPaginationPage(0);
    actions.clearOpportunitiesPets();

    return this.findEligibleOpportunities();
  };

  filterPendingContacts = async (event) => {
    if (event) event.preventDefault();

    const { from, to } = useCrmPet.getState().data.pending.filter;

    if (from && to && from > to) {
      toast.error("A data de início não pode ser posterior à data de fim.", {
        theme: "colored",
      });
      return null;
    }

    const actions = useCrmPet.getState().actions;
    actions.setPendingLoading(true);
    actions.setPendingPaginationPage(0);
    actions.clearPendingPets();

    return this.findPendingContacts();
  };

  clearPendingFilter = async () => {
    const actions = useCrmPet.getState().actions;
    actions.clearPendingFilter();
    actions.setPendingPaginationPage(0);
    actions.clearPendingPets();

    return this.findPendingContacts();
  };

  filterContactedCustomers = async (event) => {
    if (event) event.preventDefault();

    const { from, to } = useCrmPet.getState().data.contacted.filter;

    if (from && to && from > to) {
      toast.error("A data de início não pode ser posterior à data de fim.", {
        theme: "colored",
      });
      return null;
    }

    const actions = useCrmPet.getState().actions;
    actions.setContactedLoading(true);
    actions.setContactedPaginationPage(0);
    actions.clearContactedPets();

    return this.findContactedCustomers();
  };

  clearContactedFilter = async () => {
    const actions = useCrmPet.getState().actions;
    actions.clearContactedFilter();
    actions.setContactedPaginationPage(0);
    actions.clearContactedPets();

    return this.findContactedCustomers();
  };

  setPage = async (page) => {
    const state = useCrmPet.getState().data;
    const pageExists = state.customers[page];

    useCrmPet.getState().actions.setPaginationPage(page);

    if (!pageExists) await this.findPets();
  };

  setEligibleOpportunitiesPage = async (page) => {
    const { pets } = useCrmPet.getState().data.opportunities;
    const pageExists = pets[page];

    useCrmPet.getState().actions.setOpportunitiesPaginationPage(page);

    if (!pageExists) await this.findEligibleOpportunities();
  };

  setPendingPage = async (page) => {
    const { pets } = useCrmPet.getState().data.pending;
    const pageExists = pets[page];

    useCrmPet.getState().actions.setPendingPaginationPage(page);

    if (!pageExists) await this.findPendingContacts();
  };

  setContactedPage = async (page) => {
    const { pets } = useCrmPet.getState().data.contacted;
    const pageExists = pets[page];

    useCrmPet.getState().actions.setContactedPaginationPage(page);

    if (!pageExists) await this.findContactedCustomers();
  };

  handleSortColumn = async (sortBy) => {
    const { sort } = useCrmPet.getState().data;
    const sortOrder = sort.sortBy === sortBy && sort.sortOrder === "asc" ? "desc" : "asc";
    const actions = useCrmPet.getState().actions;

    actions.setSort(sortBy, sortOrder);
    actions.setPaginationPage(0);
    actions.clearCustomers();

    await this.findPets();
  };

  openConfig = () => {
    useCrmPet.getState().actions.setShowConfig(true);
  };

  closeConfig = () => {
    useCrmPet.getState().actions.setShowConfig(false);
  };

  reset = () => {
    useCrmPet.getState().actions.setFilter(CRM_PET_DEFAULT_FILTER);
  };

  syncFilterUrl = () => {
    if (typeof window === "undefined") return;
    const { rule } = useCrmPet.getState().data.filter;
    const params = new URLSearchParams(window.location.search);
    if (rule && rule !== "ALL") params.set("rule", rule);
    else params.delete("rule");
    params.delete("subtype");
    params.delete("source");
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  };
}
